import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import S3FileUpload from "react-s3/lib/ReactS3";
import { s3Config } from "../config/s3bucket";
import { compressImage } from "../utils";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getProductAdd, subCategoryList } from "../redux/actions/productAction";

const AddBranchItemModal = ({
  show,
  handleClose,
  branchName,
  branchId,
  storeId,
  storeCategoryId,
}) => {
  const dispatch = useDispatch();
  const [productName, setProductName] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [sizes, setSizes] = useState([
    { size: "", price: "", discountedPrice: "", stock: "" },
  ]);

  const [addOns, setAddOns] = useState([{ name: "", charges: "" }]);

  const [choices, setChoices] = useState([{ name: "", charges: "" }]);

  const [addOnConfig, setAddOnConfig] = useState({
    isRequired: false,
    maxSelect: 1,
  });
  const [choiceConfig, setChoiceConfig] = useState({
    isRequired: false,
    maxSelect: 1,
  });

  const [subCategoryData, setSubCategoryData] = useState([]);

  const resetForm = () => {
    setProductName("");
    setSubCategory("");
    setDescription("");
    setImage(null);
    setSizes([{ size: "", price: "", discountedPrice: "", stock: "" }]);
    setAddOns([{ name: "", charges: "" }]);
    setChoices([{ name: "", charges: "" }]);
    setAddOnConfig({ isRequired: false, maxSelect: 1 });
    setChoiceConfig({ isRequired: false, maxSelect: 1 });
  };

  useEffect(() => {
    if (show) {
      resetForm();
      let search;
      dispatch(subCategoryList({ search }))
        .then((res) => {
          if (res.status === 200) {
            setSubCategoryData(res.data || []);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch sub-categories:", err);
        });
    }
  }, [dispatch, show]);

  const handleImageUpload = async (e) => {
    const file = e?.target?.files[0];

    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!validTypes.includes(file.type)) {
      toast.error("Only jpeg, jpg and png are allowed", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    try {
      setIsUploading(true);
      const compressedFile = await compressImage(file, 0.7, 1024);
      const data = await S3FileUpload.uploadFile(compressedFile, s3Config);
      setImage(data.location);
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Image upload failed", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddSize = () =>
    setSizes([
      ...sizes,
      { size: "", price: "", discountedPrice: "", stock: "" },
    ]);
  const handleRemoveSize = (index) =>
    setSizes(sizes.filter((_, i) => i !== index));

  const handleAddAddOn = () =>
    setAddOns([...addOns, { name: "", charges: "" }]);
  const handleRemoveAddOn = (index) =>
    setAddOns(addOns.filter((_, i) => i !== index));

  const handleAddChoice = () =>
    setChoices([...choices, { name: "", charges: "" }]);
  const handleRemoveChoice = (index) =>
    setChoices(choices.filter((_, i) => i !== index));

  const handleAddProduct = () => {
    // validation
    if (!image) {
      toast.error("Product image is required");
      return;
    }
    if (!productName.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!subCategory) {
      toast.error("Sub-category is required");
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }

    // size validation
    for (let i = 0; i < sizes.length; i++) {
      const s = sizes[i];
      if (
        !s.size.trim() ||
        !s.price.toString().trim() ||
        !s.stock.toString().trim()
      ) {
        toast.error("Please fill in size, price, and stock for all sizes");
        return;
      }
      if (
        Number(s.price) < 0 ||
        Number(s.stock) < 0 ||
        (s.discountedPrice && Number(s.discountedPrice) < 0)
      ) {
        toast.error("Prices and stock values cannot be negative");
        return;
      }
    }

    // map sizes to variantsData
    const variantsData = sizes.map((item, index) => ({
      size: item.size,
      stock: item.stock,
      price: item.price,
      discountedPrice: item.discountedPrice || "0",
      default: index === 0, // mark the first one as default
      desc: description,
    }));

    // filter empty add-ons
    const filteredAddOns = addOns.filter(
      (a) => a.name.trim() !== "" || a.charges.trim() !== "",
    );
    for (let a of filteredAddOns) {
      if (!a.name.trim() || !a.charges.trim()) {
        toast.error("Please fill both name and charges for add-ons");
        return;
      }
      if (Number(a.charges) < 0) {
        toast.error("Add-on charges cannot be negative");
        return;
      }
    }

    // filter empty choices
    const filteredChoices = choices.filter(
      (c) => c.name.trim() !== "" || c.charges.trim() !== "",
    );
    for (let c of filteredChoices) {
      if (!c.name.trim() || !c.charges.trim()) {
        toast.error("Please fill both name and charges for choices");
        return;
      }
      if (Number(c.charges) < 0) {
        toast.error("Choice charges cannot be negative");
        return;
      }
    }

    const payload = {
      store: storeId,
      branchId: branchId,
      image,
      product_name: productName,
      sub_category: subCategory,
      description,
      addOns: filteredAddOns,
      variantsData,
      choices: filteredChoices,
      addOnConfig,
      choiceConfig,
      type: "",
    };

    dispatch(getProductAdd(payload))
      .then((res) => {
        if (res.status === 200) {
          toast.success(res.message || "Branch product added successfully");
          resetForm();
          handleClose();
        }
      })
      .catch((err) => {
        toast.error(err.message || "Failed to add branch product");
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="ModalBox"
      size="lg"
    >
      <Modal.Body style={{ padding: 0 }}>
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Add branch-only item
          </h4>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              color: "#999",
              cursor: "pointer",
              lineHeight: "1",
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          <div
            style={{
              backgroundColor: "#eef3f8",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #dce4ef",
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "25px",
            }}
          >
            <i
              className="fa fa-map-marker"
              style={{
                color: "#003a6b",
                marginTop: "3px",
                marginRight: "12px",
                fontSize: "18px",
              }}
            ></i>
            <p
              style={{
                margin: 0,
                fontSize: "14.5px",
                color: "#333",
                lineHeight: "1.5",
              }}
            >
              This item will appear{" "}
              <strong>only at {branchName || "this branch"}</strong> — not on
              the shared menu or any other branch. The shared menu stays the
              same everywhere.
            </p>
          </div>

          <div className="row">
            <div className="col-sm-12 mb-3">
              <div className="form-group">
                <label
                  htmlFor="branchItemImageUpload"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "8px",
                    border: "1px dashed #ccc",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#f9f9f9",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  {isUploading ? (
                    <>
                      <i
                        className="fa fa-spinner fa-spin"
                        style={{ fontSize: "24px", color: "#003a6b" }}
                      ></i>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#003a6b",
                          marginTop: "5px",
                        }}
                      >
                        Uploading...
                      </span>
                    </>
                  ) : image ? (
                    <img
                      src={image}
                      alt="Upload Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <>
                      <i
                        className="fa fa-camera"
                        style={{ fontSize: "24px", color: "#999" }}
                      ></i>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      >
                        Upload
                      </span>
                    </>
                  )}
                </label>
                <input
                  type="file"
                  id="branchItemImageUpload"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
              </div>
            </div>
            <div className="col-sm-6 mb-3">
              <div className="form-group">
                <label
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Product Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Product Name"
                  style={{ borderRadius: "6px", padding: "10px" }}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                />
              </div>
            </div>
            <div className="col-sm-6 mb-3">
              <div className="form-group">
                <label
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Select Sub-Category
                </label>
                <select
                  className="form-control"
                  style={{
                    borderRadius: "6px",
                    padding: "10px",
                    height: "auto",
                  }}
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                >
                  <option value="">Select</option>
                  {subCategoryData ? (
                    subCategoryData.map((ele) => (
                      <option key={ele?._id} value={ele?._id}>
                        {ele?.sub_category_name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No Category found
                    </option>
                  )}
                </select>
              </div>
            </div>
            <div className="col-sm-12 mb-3">
              <div className="form-group">
                <label
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Description
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Description...."
                  style={{ borderRadius: "6px", padding: "10px" }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="col-sm-12 mb-3">
              <div
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              >
                <h5
                  style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    marginBottom: "15px",
                  }}
                >
                  Sizes
                </h5>
                {sizes.map((item, index) => (
                  <div
                    className="row mb-2"
                    key={index}
                    style={{ position: "relative" }}
                  >
                    <div className="col-sm-3">
                      <label style={{ fontSize: "13px" }}>
                        Size Specification
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Regular"
                        value={item.size}
                        onChange={(e) => {
                          const newSizes = [...sizes];
                          newSizes[index].size = e.target.value;
                          setSizes(newSizes);
                        }}
                      />
                    </div>
                    <div className="col-sm-3">
                      <label style={{ fontSize: "13px" }}>
                        Selling Price (SAR)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={item.price}
                        onChange={(e) => {
                          const newSizes = [...sizes];
                          newSizes[index].price = e.target.value;
                          setSizes(newSizes);
                        }}
                      />
                    </div>
                    <div className="col-sm-3">
                      <label style={{ fontSize: "13px" }}>
                        Discounted Price (SAR)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        disabled
                        value={item.discountedPrice}
                        onChange={(e) => {
                          const newSizes = [...sizes];
                          newSizes[index].discountedPrice = e.target.value;
                          setSizes(newSizes);
                        }}
                      />
                    </div>
                    <div
                      className="col-sm-3"
                      style={{ display: "flex", alignItems: "flex-end" }}
                    >
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "13px" }}>Stock</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="0"
                          value={item.stock}
                          onChange={(e) => {
                            const newSizes = [...sizes];
                            newSizes[index].stock = e.target.value;
                            setSizes(newSizes);
                          }}
                        />
                      </div>
                      {sizes.length > 1 && (
                        <button
                          onClick={() => handleRemoveSize(index)}
                          style={{
                            border: "none",
                            background: "none",
                            color: "red",
                            padding: "10px",
                            marginLeft: "5px",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleAddSize}
                  className="btn btn-link p-0 mt-2"
                  style={{
                    color: "#003a6b",
                    fontWeight: "bold",
                    textDecoration: "none",
                  }}
                >
                  + Add More Sizes
                </button>
              </div>
            </div>

            <div className="col-sm-6 mb-3">
              <div
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              >
                <h5
                  style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    marginBottom: "15px",
                  }}
                >
                  Add Ons
                </h5>
                {addOns.map((item, index) => (
                  <div className="row mb-2" key={index}>
                    <div className="col-sm-5">
                      <label style={{ fontSize: "13px" }}>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.name}
                        onChange={(e) => {
                          const newAddOns = [...addOns];
                          newAddOns[index].name = e.target.value;
                          setAddOns(newAddOns);
                        }}
                      />
                    </div>
                    <div className="col-sm-5">
                      <label style={{ fontSize: "13px" }}>Charges (SAR)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.charges}
                        onChange={(e) => {
                          const newAddOns = [...addOns];
                          newAddOns[index].charges = e.target.value;
                          setAddOns(newAddOns);
                        }}
                      />
                    </div>
                    <div
                      className="col-sm-2"
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                      }}
                    >
                      {addOns.length > 1 && (
                        <button
                          onClick={() => handleRemoveAddOn(index)}
                          style={{
                            border: "none",
                            background: "none",
                            color: "red",
                            padding: "10px",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleAddAddOn}
                  className="btn btn-link p-0 mt-2"
                  style={{
                    color: "#003a6b",
                    fontWeight: "bold",
                    textDecoration: "none",
                  }}
                >
                  + Add More
                </button>
              </div>
            </div>

            <div className="col-sm-6 mb-3">
              <div
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              >
                <h5
                  style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    marginBottom: "15px",
                  }}
                >
                  Choices
                </h5>
                {choices.map((item, index) => (
                  <div className="row mb-2" key={index}>
                    <div className="col-sm-5">
                      <label style={{ fontSize: "13px" }}>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.name}
                        onChange={(e) => {
                          const newChoices = [...choices];
                          newChoices[index].name = e.target.value;
                          setChoices(newChoices);
                        }}
                      />
                    </div>
                    <div className="col-sm-5">
                      <label style={{ fontSize: "13px" }}>Charges (SAR)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.charges}
                        onChange={(e) => {
                          const newChoices = [...choices];
                          newChoices[index].charges = e.target.value;
                          setChoices(newChoices);
                        }}
                      />
                    </div>
                    <div
                      className="col-sm-2"
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                      }}
                    >
                      {choices.length > 1 && (
                        <button
                          onClick={() => handleRemoveChoice(index)}
                          style={{
                            border: "none",
                            background: "none",
                            color: "red",
                            padding: "10px",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleAddChoice}
                  className="btn btn-link p-0 mt-2"
                  style={{
                    color: "#003a6b",
                    fontWeight: "bold",
                    textDecoration: "none",
                  }}
                >
                  + Add More
                </button>
              </div>
            </div>

            <div className="col-sm-12 mb-3">
              <div
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              >
                <h5
                  style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    marginBottom: "15px",
                    color: "#003a6b",
                  }}
                >
                  Configurations
                </h5>
                <div className="row">
                  <div
                    className="col-sm-6"
                    style={{ borderRight: "1px solid #eee" }}
                  >
                    <h6
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        marginBottom: "10px",
                        color: "#003a6b",
                      }}
                    >
                      Add Ons Configuration
                    </h6>
                    <div className="form-group mb-2">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#475569",
                          textTransform: "none",
                          letterSpacing: "normal",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={addOnConfig.isRequired}
                          onChange={(e) =>
                            setAddOnConfig({
                              ...addOnConfig,
                              isRequired: e.target.checked,
                            })
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            accentColor: "#003a6b",
                          }}
                        />
                        <span>Is Required</span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: "13px", color: "#666" }}>
                        Max Select
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={addOnConfig.maxSelect}
                        onChange={(e) =>
                          setAddOnConfig({
                            ...addOnConfig,
                            maxSelect: parseInt(e.target.value) || 1,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <h6
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        marginBottom: "10px",
                        color: "#003a6b",
                      }}
                    >
                      Choice Configuration
                    </h6>
                    <div className="form-group mb-2">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#475569",
                          textTransform: "none",
                          letterSpacing: "normal",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={choiceConfig.isRequired}
                          onChange={(e) =>
                            setChoiceConfig({
                              ...choiceConfig,
                              isRequired: e.target.checked,
                            })
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            accentColor: "#003a6b",
                          }}
                        />
                        <span>Is Required</span>
                      </label>
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: "13px", color: "#666" }}>
                        Max Select
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={choiceConfig.maxSelect}
                        onChange={(e) =>
                          setChoiceConfig({
                            ...choiceConfig,
                            maxSelect: parseInt(e.target.value) || 1,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "20px 24px",
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: "15px",
            backgroundColor: "#fff",
            borderBottomLeftRadius: "5px",
            borderBottomRightRadius: "5px",
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "10px 24px",
              backgroundColor: "#fff",
              color: "#003a6b",
              border: "1px solid #003a6b",
              borderRadius: "6px",
              fontWeight: "bold",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            onClick={handleAddProduct}
            style={{
              padding: "10px 24px",
              backgroundColor: "#003a6b",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontWeight: "bold",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            ADD ITEM
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddBranchItemModal;
